import { useCallback } from "react";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";
import { loadProcess, type LoadedProcess } from "../../workflows/data/processCatalogue";
import { projectProcessReader } from "../../workflows/data/projectProcessReader";

/**
 * The Thread's Workflow with its steps, read once per Thread page: the steps panel, the
 * graph and the log name steps from it. `null` for a Thread without a Workflow. With
 * `contentCommit`, the Thread's bound content version, roles resolve as the run reads them
 * (F103); without it, at the latest version.
 */
export function useThreadProcess(workflow: string | null, projectId: string, contentCommit: string | null = null): LoadHandle<LoadedProcess | null> {
  const load = useCallback(
    (signal: AbortSignal) => (workflow === null ? Promise.resolve(null) : loadProcess(workflow, projectProcessReader(projectId, signal, contentCommit))),
    [workflow, projectId, contentCommit],
  );
  return useLoad(load);
}

/**
 * The Workflows the Thread followed before it switched (F126), by version, read as its own
 * is: their steps name the Thread's earlier entries. Empty while none was left or they load.
 */
export function useEarlierProcesses(workflows: readonly string[], projectId: string, contentCommit: string | null): ReadonlyMap<string, LoadedProcess> {
  const joined = workflows.join(" ");
  const load = useCallback(
    async (signal: AbortSignal) => {
      const reader = projectProcessReader(projectId, signal, contentCommit);
      const references = joined === "" ? [] : joined.split(" ");
      const processes = await Promise.all(references.map((workflow) => loadProcess(workflow, reader)));
      return new Map(references.map((workflow, index) => [workflow, processes[index]] as const).filter((pair): pair is readonly [string, LoadedProcess] => pair[1] !== undefined));
    },
    [joined, projectId, contentCommit],
  );
  const { state } = useLoad(load);
  return state.status === "loaded" ? state.value : NO_PROCESSES;
}

const NO_PROCESSES: ReadonlyMap<string, LoadedProcess> = new Map();
